import React, { useEffect, useMemo, useState } from "react";
import { PanelHelp } from "./QuickStart";
import { Printer, LineChart } from "lucide-react";
import { getTickets, onTicketHistoryChange } from "../lib/ticketHistory";
import { computeStats, PERIODS, periodLabel, KINDS, kindLabel } from "../lib/reportStats";

const fmtMoney = (n, dollar = true) =>
  `${n < 0 ? "-" : ""}${dollar ? "$" : ""}${Math.abs(n).toFixed(2)}`;

const fmtPct = (n) => `${n.toFixed(2)}%`;

const fmtRange = ({ from, to }) => {
  const opts = { month: "short", day: "numeric" };
  const fromStr = from.toLocaleDateString(undefined, opts);
  const toStr = to.toLocaleDateString(undefined, opts);
  return fromStr === toStr ? fromStr : `${fromStr} – ${toStr}`;
};

const StatBlock = ({ label, value, sub, testId, color = "#f5ecd6" }) => (
  <div
    data-testid={testId}
    className="p-4 rounded-md ring-1 ring-stone-700/60"
    style={{ background: "#141414" }}
  >
    <div
      className="text-[10px] uppercase tracking-[0.22em] text-stone-400 mb-1"
      style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
    >
      {label}
    </div>
    <div
      className="text-2xl font-bold"
      style={{
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        color,
      }}
    >
      {value}
    </div>
    {sub && (
      <div className="text-[10px] text-stone-500 mt-1 tracking-[0.14em] uppercase"
        style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
        {sub}
      </div>
    )}
  </div>
);

export const PerformanceReport = () => {
  const [tickets, setTickets] = useState([]);
  const [period, setPeriod] = useState("day");
  const [kind, setKind] = useState("all");

  useEffect(() => {
    setTickets(getTickets());
    const refresh = () => setTickets(getTickets());
    const unsub = onTicketHistoryChange(refresh);
    return unsub;
  }, []);

  const stats = useMemo(
    () => computeStats(tickets, period, kind),
    [tickets, period, kind]
  );

  const roiColor =
    stats.roi > 0 ? "#84cc16" : stats.roi < 0 ? "#fca5a5" : "#f5ecd6";
  const netColor =
    stats.totalNet > 0 ? "#84cc16" : stats.totalNet < 0 ? "#fca5a5" : "#f5ecd6";

  return (
    <section
      data-testid="performance-report"
      className="mt-8 rounded-xl overflow-hidden ring-1 ring-stone-800"
      style={{ background: "#0a0a0a" }}
    >
      <header className="px-5 py-3 border-b border-stone-800 flex items-center justify-between flex-wrap gap-3 print:border-b-2 print:border-black">
        <div className="flex items-center gap-3">
          <LineChart size={18} className="text-[#84cc16]" />
          <h2
            className="text-2xl text-[#f5ecd6]"
            style={{ fontFamily: "'Instrument Serif', 'Times New Roman', serif" }}
          >
            Performance Report
          </h2>
          <PanelHelp step={7} label="the Performance Report" />
          <span
            data-testid="report-range"
            className="text-[11px] uppercase tracking-[0.2em] text-stone-400"
            style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
          >
            {kindLabel(kind)} · {periodLabel(period)} · {fmtRange(stats)}
          </span>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          {PERIODS.map((p) => (
            <button
              key={p}
              data-testid={`report-period-${p}`}
              onClick={() => setPeriod(p)}
              className={`px-3 h-8 text-[11px] tracking-[0.16em] uppercase rounded border transition ${
                period === p
                  ? "bg-[#84cc16] text-[#0a0a0a] border-[#365314]"
                  : "bg-transparent text-stone-300 border-stone-700 hover:border-stone-500"
              }`}
              style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
            >
              {periodLabel(p)}
            </button>
          ))}
          <button
            data-testid="report-print-btn"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 px-3 h-8 text-[11px] tracking-[0.16em] uppercase rounded border border-[#f5ecd6] text-[#f5ecd6] hover:bg-[#f5ecd6] hover:text-[#0a0a0a] transition"
            style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
          >
            <Printer size={12} />
            Print
          </button>
        </div>
      </header>
      <div
        data-testid="report-kind-tabs"
        className="px-5 pt-4 pb-1 flex flex-wrap items-center gap-2 print:hidden"
      >
        {KINDS.map((k) => (
          <button
            key={k}
            data-testid={`report-kind-${k}`}
            onClick={() => setKind(k)}
            className={`px-3 h-8 text-[11px] tracking-[0.16em] uppercase rounded-full border transition ${
              kind === k
                ? "bg-[#f5ecd6] text-[#0a0a0a] border-[#f5ecd6]"
                : "bg-transparent text-stone-300 border-stone-700 hover:border-stone-500"
            }`}
            style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
          >
            {kindLabel(k)}
          </button>
        ))}
      </div>
      <div className="p-5 grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
        <StatBlock
          testId="stat-avg-ticket"
          label="Avg Ticket Amount"
          value={fmtMoney(stats.avgTicket)}
          sub={`${stats.totalTickets} tickets · ${fmtMoney(stats.totalWagered)} wagered`}
        />
        <StatBlock
          testId="stat-avg-payout"
          label="Avg Payout / Hit"
          value={fmtMoney(stats.avgPayout)}
          sub={`${stats.hitCount} hits · ${fmtMoney(stats.totalPayout)} paid`}
          color="#84cc16"
        />
        <StatBlock
          testId="stat-ratio"
          label="Payout : Ticket Ratio"
          value={
            stats.payoutToTicketRatio > 0
              ? `${stats.payoutToTicketRatio.toFixed(2)}×`
              : "—"
          }
          sub="Avg payout / Avg wager"
        />
        <StatBlock
          testId="stat-winpct"
          label="Win Rate"
          value={stats.scoredTickets > 0 ? fmtPct(stats.winPct) : "—"}
          sub={`${stats.hitCount} of ${stats.scoredTickets} scored`}
        />
        <StatBlock
          testId="stat-roi"
          label="ROI"
          value={stats.totalWagered > 0 ? fmtPct(stats.roi) : "—"}
          sub={`Net ${fmtMoney(stats.totalNet)}`}
          color={roiColor}
        />
      </div>
      <div className="px-5 pb-4 text-[10px] text-stone-500 tracking-[0.14em] uppercase print:text-black"
        style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
        Win rate + ROI reflect only <span style={{color: netColor}}>scored</span> tickets.
        Archive a ticket + click Score after each race to see it counted here.
      </div>
    </section>
  );
};
